/**
 * Copyright 2017 OpenStack Foundation
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * */

import { LOGOUT_USER } from "openstack-uicore-foundation/lib/security/actions";
import {
  DATETIME_FORMAT,
  DEFAULT_PER_PAGE,
  FORM_STATUS
} from "../utils/constants";
import {
  RECEIVE_SPONSOR_FORMS,
  RECEIVE_SPONSOR_FORMS_APPEND
} from "../actions/sponsor-actions";
import { formatDate } from "../utils/methods";

const DEFAULT_STATE = {
  forms: [],
  order: "id",
  orderDir: 1,
  currentPage: 1,
  lastPage: 1,
  perPage: DEFAULT_PER_PAGE,
  total: 0
};

const sponsorFormsListReducer = (state = DEFAULT_STATE, action) => {
  const { type, payload } = action;

  switch (type) {
    case LOGOUT_USER:
      return DEFAULT_STATE;
    case RECEIVE_SPONSOR_FORMS: {
      const {
        data,
        total,
        last_page: lastPage,
        per_page: perPage,
        current_page: currentPage
      } = payload.response;

      const forms = reduceFormsAddons(data);

      return {
        ...state,
        forms,
        total,
        lastPage,
        perPage,
        currentPage
      };
    }
    case RECEIVE_SPONSOR_FORMS_APPEND: {
      const normalizedForms = reduceFormsAddons(payload);

      return {
        ...state,
        forms: [...state.forms, ...normalizedForms],
        currentPage: state.lastPage
      };
    }
    default:
      return state;
  }
};

const mapFormData = (form) => ({
  id: form.form_id,
  addon_id: "none",
  code: form.code,
  name: form.name,
  addon: "-",
  // the portal shows dates in the user's local timezone
  opens_at: form.opens_at
    ? formatDate(form.opens_at, "LOC", DATETIME_FORMAT)
    : "-",
  expires_at: form.expires_at
    ? formatDate(form.expires_at, "LOC", DATETIME_FORMAT)
    : "-",
  items_count: `${form.items_count} item${form.items_count === 1 ? "" : "s"}`,
  status: form.status,
  is_closed: form.status === FORM_STATUS.CLOSED,
  is_locked: form.is_locked ?? false,
  is_read_only: form.is_locked || form.status === FORM_STATUS.CLOSED,
  in_cart: form.in_cart ?? false,
  cart_form_id: form.cart_form_id ?? null
});

const reduceFormsAddons = (data) =>
  data.reduce((res, it) => {
    if (it.allowed_add_ons.length > 0) {
      const newItems = it.allowed_add_ons.map((a) => ({
        ...mapFormData(it),
        addon: a.name,
        addon_id: a.id,
        in_cart: a.in_cart ?? false,
        cart_form_id: a.cart_form_id ?? null
      }));
      return [...res, ...newItems];
    }

    return [...res, mapFormData(it)];
  }, []);

export default sponsorFormsListReducer;
